import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { database } from "../../firebase";
import { ref, get, update, remove, onValue } from "firebase/database";
import { useAuth } from "../../components/AuthContext";
import {
  FaCalendarAlt,
  FaUser,
  FaEye,
  FaArrowLeft,
  FaEdit,
  FaTrash,
  FaShare,
  FaPrint,
  FaBookmark,
  FaComments,
  FaChevronUp,
  FaChevronDown,

} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";


// YouTube 임베드 컴포넌트와 contentUtils는 그대로 유지

// YouTube 임베드 컴포넌트
const YouTubeEmbed = ({ videoId }) => {
  if (!videoId) return null;

  return (
    <div
      className="relative rounded-2xl overflow-hidden shadow-xl mb-10 transform transition-transform hover:scale-[1.01]"
      style={{ paddingBottom: "56.25%" }}
    >
      <iframe
        src={`https://www.youtube.com/embed/${videoId}`}
        title="YouTube video player"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="absolute top-0 left-0 w-full h-full"
      />
    </div>
  );
};

// 컨텐츠 처리 유틸리티 함수들
const contentUtils = {
  extractYouTubeId: (content) => {
    if (!content) return null;
    const urlRegex = /https:\/\/youtu\.be\/([a-zA-Z0-9_-]{11})/;
    const match = content.match(urlRegex);
    return match ? match[1] : null;
  },

  processContent: (content) => {
    if (!content) return "";

    let processedContent = content;

    // YouTube 임베드 태그 제거
    processedContent = processedContent
      .replace(/<oembed.*?<\/oembed>/g, "")
      .trim();

    const linkStyle = "text-blue-600 hover:text-blue-800 hover:underline";
    const linkAttributes = 'target="_blank" rel="noopener noreferrer"';

    // 이미지 태그만 있는 링크 처리
    processedContent = processedContent.replace(
      /<a[^>]*>(<img[^>]*>)<\/a>/g,
      "$1"
    );

    // 이미지 태그 처리 - 더 세련된 스타일 적용
    processedContent = processedContent.replace(
      /<img\s+src="([^"]+)"[^>]*>/g,
      '<div class="my-10 relative group">' +
        '<img src="$1" class="rounded-2xl w-full h-auto shadow-xl transition-all duration-300 group-hover:shadow-2xl" alt="" />' +
        '<div class="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>' +
        "</div>"
    );

    // URL 디코딩 처리
    processedContent = processedContent.replace(
      /href="([^"]+)"/g,
      (match, url) => `href="${decodeURIComponent(url.replace(/&amp;/g, "&"))}"`
    );

    // 일반 URL을 링크로 변환
    processedContent = processedContent.replace(
      /(?<!["'])(?<!href=["'])(https?:\/\/(?!youtu\.be)[^\s<>"]+)(?![^<]*>|[^<]*<\/a>)/g,
      `<a href="$1" ${linkAttributes} class="${linkStyle} inline-flex items-center"><span>$1</span><svg class="w-3.5 h-3.5 ml-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5a.75.75 0 00-.75-.75h-7.5a.75.75 0 000 1.5h5.69l-7.22 7.22a.75.75 0 000 1.06z" clip-rule="evenodd" /></svg></a>`
    );

    return processedContent;
  },
};

// 게시판 타입별 테마 설정
const getBoardTheme = (type) => {
  switch (type) {
    case "notice":
      return {
        primary: "from-blue-600 to-indigo-700",
        secondaryFrom: "from-blue-50",
        secondaryTo: "to-indigo-50",
        accent: "bg-gradient-to-r from-blue-600 to-indigo-700",
        light: "bg-gradient-to-br from-blue-50 to-indigo-50",
        hover: "hover:from-blue-700 hover:to-indigo-800",
        text: "text-blue-600",
        border: "border-blue-200",
        badge: "from-blue-500 to-indigo-600 text-white",
        soft: "bg-blue-50",
      };
    case "faq":
      return {
        primary: "from-green-600 to-teal-700",
        secondaryFrom: "from-green-50",
        secondaryTo: "to-teal-50",
        accent: "bg-gradient-to-r from-green-600 to-teal-700",
        light: "bg-gradient-to-br from-green-50 to-teal-50",
        hover: "hover:from-green-700 hover:to-teal-800",
        text: "text-green-600",
        border: "border-green-200",
        badge: "from-green-500 to-teal-600 text-white",
        soft: "bg-green-50",
      };
    case "qna":
      return {
        primary: "from-purple-600 to-indigo-700",
        secondaryFrom: "from-purple-50",
        secondaryTo: "to-indigo-50",
        accent: "bg-gradient-to-r from-purple-600 to-indigo-700",
        light: "bg-gradient-to-br from-purple-50 to-indigo-50",
        hover: "hover:from-purple-700 hover:to-indigo-800",
        text: "text-purple-600",
        border: "border-purple-200",
        badge: "from-purple-500 to-indigo-600 text-white",
        soft: "bg-purple-50",
      };
    default:
      return {
        primary: "from-gray-600 to-gray-700",
        secondaryFrom: "from-gray-50",
        secondaryTo: "to-gray-100",
        accent: "bg-gradient-to-r from-gray-600 to-gray-700",
        light: "bg-gradient-to-br from-gray-50 to-gray-100",
        hover: "hover:from-gray-700 hover:to-gray-800",
        text: "text-gray-600",
        border: "border-gray-200",
        badge: "from-gray-500 to-gray-600 text-white",
        soft: "bg-gray-50",
      };
  }
};

const BoardDetail = () => {
  const { id, type = "notice" } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user, isAdmin } = useAuth();
  const [boardTypeName, setBoardTypeName] = useState("");
  const [showScrollTop, setShowScrollTop] = useState(false);

  const [prevPost, setPrevPost] = useState(null); // 이전 게시글
  const [nextPost, setNextPost] = useState(null); // 다음 게시글

  // 게시판 테마 적용
  const theme = getBoardTheme(type);

  // 스크롤 이벤트 리스너
  useEffect(() => {
    const checkScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener("scroll", checkScroll);
    return () => window.removeEventListener("scroll", checkScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // 모든 게시글 목록 가져오기
  useEffect(() => {
    const postsRef = ref(database, `posts/${type}`);
    
    onValue(postsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        // Board.js와 동일한 방식으로 게시글 정렬 (최신순)
        const postList = Object.entries(data)
          .reverse()
          .map(([postId, value]) => ({ id: postId, ...value }));
        

        
        // 현재 게시글의 인덱스 찾기
        const currentIndex = postList.findIndex(post => post.id === id);
        
        // 이전글, 다음글 설정
        if (currentIndex > 0) {
          setNextPost(postList[currentIndex - 1]); // 최신글이 위에 있으므로 인덱스가 작은 것이 다음글
        }
        
        if (currentIndex < postList.length - 1) {
          setPrevPost(postList[currentIndex + 1]); // 이전글은 인덱스가 큰 것
        }
      }
    });
  }, [type, id]);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        // 게시판 타입 이름 가져오기
        const boardTypeRef = ref(database, `boardTypes/${type}`);
        const boardTypeSnapshot = await get(boardTypeRef);
        if (boardTypeSnapshot.exists()) {
          setBoardTypeName(boardTypeSnapshot.val());
        }

        // 게시글 가져오기
        const postRef = ref(database, `posts/${type}/${id}`);
        const snapshot = await get(postRef);

        if (!snapshot.exists()) {
          alert("해당 게시글이 존재하지 않습니다.");
          return navigate(`/board/${type}`);
        }

        const postData = snapshot.val();
        setPost(postData);
        update(postRef, { views: (postData.views || 0) + 1 });
      } catch (error) {
        console.error("게시글 로딩 중 오류:", error);
        alert("게시글을 불러오는데 실패했습니다.");
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id, type, navigate]);

  const handleDelete = async () => {
    if (!window.confirm("정말로 이 게시글을 삭제하시겠습니까?")) return;

    try {
      await remove(ref(database, `posts/${type}/${id}`));
      navigate(`/board/${type}`);
    } catch (error) {
      console.error("삭제 중 오류:", error);
      alert("게시글 삭제에 실패했습니다.");
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard
      .writeText(url)
      .then(() => {
        // 복사 성공 애니메이션 표시
        const copyMsg = document.createElement("div");
        copyMsg.className =
          "fixed bottom-4 right-4 bg-black text-white px-4 py-2 rounded-lg shadow-lg z-50 animate-fade-in-out";
        copyMsg.textContent = "링크가 클립보드에 복사되었습니다!";
        document.body.appendChild(copyMsg);

        setTimeout(() => {
          document.body.removeChild(copyMsg);
        }, 2000);
      })
      .catch((err) => alert("링크 복사 실패: " + err));
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 relative">
            <div className="absolute inset-0 rounded-full border-t-2 border-b-2 border-blue-500 animate-spin"></div>
            <div className="absolute inset-2 rounded-full border-r-2 border-l-2 border-indigo-500 animate-spin animation-delay-150"></div>
            <div className="absolute inset-4 rounded-full border-t-2 border-b-2 border-purple-500 animate-spin animation-delay-300"></div>
          </div>
          <p className="mt-4 text-gray-600 animate-pulse">
            게시글을 불러오는 중입니다...
          </p>
        </div>
      </div>
    );
  }

  const canModify = user && (isAdmin || (post && user.email === post.author));
  const youtubeId = contentUtils.extractYouTubeId(post.content);
  const authorName = isAdmin ? "관리자" : post.author.split("@")[0];



  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen py-12 px-4 sm:px-6 lg:px-8 relative">
      {/* 백그라운드 패턴 */}
      <div className="absolute inset-0 z-0 opacity-5 pointer-events-none">
        <svg width="100%" height="100%">
          <pattern
            id="pattern-circles"
            x="0"
            y="0"
            width="50"
            height="50"
            patternUnits="userSpaceOnUse"
            patternContentUnits="userSpaceOnUse"
          >
            <circle
              id="pattern-circle"
              cx="10"
              cy="10"
              r="1.6257413380501518"
              fill="#000"
            ></circle>
          </pattern>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="url(#pattern-circles)"
          ></rect>
        </svg>
      </div>

      <div className="max-w-5xl mx-auto relative z-10">
        {/* 상단 경로 및 카테고리 */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center"
        >
          <div className="flex items-center mb-4 sm:mb-0">
            <button
              onClick={() => navigate(`/board/${type}`)}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors bg-white px-3 py-1.5 rounded-full shadow-sm hover:shadow border border-gray-200"
            >
              <FaArrowLeft className="inline mr-2" />
              <span>목록으로</span>
            </button>
            <span className="text-gray-400 mx-2">/</span>
            <span
              className={`bg-gradient-to-r ${theme.badge} px-3 py-1 rounded-full text-sm font-medium shadow-sm`}
            >
              {boardTypeName}
            </span>
          </div>

          {/* 게시글 작업 버튼 */}
          <div className="flex space-x-2">
            <motion.button
              onClick={handleShare}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              className="text-gray-500 hover:text-blue-600 p-2 rounded-full hover:bg-blue-50 transition-colors shadow-sm bg-white border border-gray-200"
              title="공유하기"
            >
              <FaShare />
            </motion.button>
            <motion.button
              onClick={handlePrint}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              className="text-gray-500 hover:text-blue-600 p-2 rounded-full hover:bg-blue-50 transition-colors shadow-sm bg-white border border-gray-200"
              title="인쇄하기"
            >
              <FaPrint />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              className="text-gray-500 hover:text-yellow-600 p-2 rounded-full hover:bg-yellow-50 transition-colors shadow-sm bg-white border border-gray-200"
              title="북마크"
            >
              <FaBookmark />
            </motion.button>
          </div>
        </motion.div>

        {/* 메인 게시글 영역 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* 게시글 본문 카드 */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            {/* 게시글 헤더 */}
            <div
              className={`bg-gradient-to-r ${theme.primary} px-8 py-6 text-white`}
            >
              <motion.h1
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="text-2xl sm:text-3xl font-bold mb-4 text-white drop-shadow-lg"
              >
                {post.title}
              </motion.h1>

              <div className="flex flex-wrap items-center text-sm text-white/90">
                <div className="flex items-center mr-6 mb-2 sm:mb-0">
                  <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white mr-3">
                    <FaUser className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-medium text-white">{authorName}</div>
                    <div className="text-xs text-white/70">{post.author}</div>
                  </div>
                </div>

                <div className="flex space-x-4">
                  <span className="flex items-center">
                    <FaCalendarAlt className="mr-2 text-white/70" />
                    {post.createdAt}
                  </span>
                  <span className="flex items-center">
                    <FaEye className="mr-2 text-white/70" />
                    {post.views || 0}
                  </span>
                  <span className="flex items-center">
                    <FaComments className="mr-2 text-white/70" />0
                  </span>
                </div>
              </div>
            </div>

            {/* 게시글 내용 */}
            <div className="p-8">
              {youtubeId && <YouTubeEmbed videoId={youtubeId} />}

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                dangerouslySetInnerHTML={{
                  __html: contentUtils.processContent(post.content),
                }}
                className="prose prose-lg max-w-none text-gray-700"
              />

              {/* 태그 영역 */}
              {/* <div className="mt-10 pt-6 border-t border-gray-100">
                <div className="flex items-center flex-wrap gap-2">
                  <FaTag className="text-gray-400 mr-2" />
                  {tags.map((tag, index) => (
                    <motion.span
                      key={index}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm bg-gradient-to-r ${theme.secondaryFrom} ${theme.secondaryTo} ${theme.text} hover:shadow-md transition-all duration-200 cursor-pointer`}
                    >
                      #{tag}
                    </motion.span>
                  ))}
                </div>
              </div> */}
            </div>
          </div>

          {/* 버튼 그룹 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.4 }}
            className="mt-8 flex flex-col sm:flex-row justify-between items-center"
          >
            <button
              onClick={() => navigate(`/board/${type}`)}
              className="w-full sm:w-auto mb-4 sm:mb-0 flex items-center justify-center px-6 py-3 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors shadow-sm"
            >
              <FaArrowLeft className="mr-2" /> 목록으로
            </button>

            {canModify && (
              <div className="flex flex-col sm:flex-row w-full sm:w-auto space-y-3 sm:space-y-0 sm:space-x-3">
                <button
                  onClick={() => navigate(`/board/${type}/edit/${id}`)}
                  className={`w-full sm:w-auto flex items-center justify-center px-6 py-3 rounded-lg text-white bg-gradient-to-r ${theme.primary} hover:shadow-lg transition-all duration-200`}
                >
                  <FaEdit className="mr-2" /> 수정하기
                </button>
                <button
                  onClick={handleDelete}
                  className="w-full sm:w-auto flex items-center justify-center px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-lg text-white transition-all duration-200 hover:shadow-lg"
                >
                  <FaTrash className="mr-2" /> 삭제하기
                </button>
              </div>
            )}
          </motion.div>

          {/* 이전글/다음글 네비게이션 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.4 }}
            className="mt-10 bg-white rounded-xl shadow-md overflow-hidden border border-gray-100"
          >
            <div className="divide-y divide-gray-100">
              {/* 이전글 */}
              <div className={`flex items-center px-6 py-4 transition-colors group ${prevPost ? 'hover:bg-gray-50 cursor-pointer' : 'opacity-50 cursor-default'}`}
                   onClick={() => prevPost && navigate(`/board/${type}/${prevPost.id}`)}>
                <FaChevronUp className={`${prevPost ? 'text-gray-400 group-hover:text-blue-500' : 'text-gray-300'} mr-3 transition-colors`} />
                <span className="font-medium text-gray-500 w-20">이전글</span>
                {prevPost ? (
                  <span className={`${theme.text} group-hover:text-blue-600 transition-colors truncate`}>
                    {prevPost.title}
                  </span>
                ) : (
                  <span className="text-gray-400">이전 글이 없습니다.</span>
                )}
              </div>
              
              {/* 다음글 */}
              <div className={`flex items-center px-6 py-4 transition-colors group ${nextPost ? 'hover:bg-gray-50 cursor-pointer' : 'opacity-50 cursor-default'}`}
                   onClick={() => nextPost && navigate(`/board/${type}/${nextPost.id}`)}>
                <FaChevronDown className={`${nextPost ? 'text-gray-400 group-hover:text-blue-500' : 'text-gray-300'} mr-3 transition-colors`} />
                <span className="font-medium text-gray-500 w-20">다음글</span>
                {nextPost ? (
                  <span className={`${theme.text} group-hover:text-blue-600 transition-colors truncate`}>
                    {nextPost.title}
                  </span>
                ) : (
                  <span className="text-gray-400">다음 글이 없습니다.</span>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* 스크롤 탑 버튼 */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            onClick={scrollToTop}
            className="fixed bottom-6 right-6 w-12 h-12 rounded-full bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-lg flex items-center justify-center z-50 hover:shadow-xl transition-shadow"
          >
            <FaChevronUp />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BoardDetail;